/**
 * Faithful port of the casino front's `getSEOFooterContent` (`src/page-components/utils/evaluatePropFunction.ts`),
 * quirks included, so the hub shows exactly the footer rule the site will pick:
 * - `*` sorts last, but `compare(*, *)` is 1 in both directions (an inconsistent comparator; Workstream B excludes
 *   the system `*` when the owner has one, so at most one `*` reaches the matcher);
 * - more segments first, then `localeCompare`; equal paths keep input order (stable sort);
 * - a rule with `*` matches by `pathName.includes(prefix)` (so `/casino/*` also matches `/x/casino/y`), others by `===`.
 */

export interface FooterRulePath {
  path: string;
}

/** Exactly the site's comparator. */
export const compareFooterRules = (a: FooterRulePath, b: FooterRulePath): number => {
  const pathA = a.path;
  const pathB = b.path;

  if (pathA === '*') {
    return 1;
  } else if (pathB === '*') {
    return -1;
  } else {
    const segmentsA = pathA.split('/').filter((s) => s !== '').length;
    const segmentsB = pathB.split('/').filter((s) => s !== '').length;

    if (segmentsA !== segmentsB) {
      return segmentsB - segmentsA;
    } else {
      return pathA.localeCompare(pathB);
    }
  }
};

export const orderFooterRules = <R extends FooterRulePath>(rules: readonly R[]): R[] =>
  [...rules].sort(compareFooterRules);

/** The first ordered rule that matches `pathName`, as the site picks it. */
export const matchFooterRule = <R extends FooterRulePath>(rules: readonly R[], pathName: string): R | undefined =>
  orderFooterRules(rules).find((rule) => {
    const isAsteriskPresent = rule.path.includes('*');
    const strippedPath = isAsteriskPresent ? rule.path.split('*')[0] : rule.path;
    return isAsteriskPresent ? pathName.includes(strippedPath) : pathName === strippedPath;
  });
